// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import React from 'react';
import Alert from '@react/react-spectrum/Icon/Alert';
import AppsUtil from './../AppsUtil';
import CSSUtil from './../CSSUtil';
import CompatibilityUtil from './../CompatibilityUtil';
import DeviceDetectionUtil from './../DeviceDetectionUtil';
import {isTestExperience, getExperienceForCampaign, EXPERIENCE_TYPE} from './../TestUtil';

import {
	ACTION_IDS,
	DEFAULT_TASK_ID,
	ALL_APPS,
	RECOMMENDED,
	CCPP_PLAN,
	CCPPN_PLAN,
	ALL_APPS_PLAN,
	SUBSTANCE_COLLECTION_PLAN,
	SUBSTANCE_TEXTURING_PLAN,
	ALL_APPS_APP_PICKER_QUERY_PARAM,
	CCPP_APP_PICKER_QUERY_PARAM,
	CCPPN_APP_PICKER_QUERY_PARAM,
	SUBSTANCE_COLLECTION_APP_PICKER_QUERY_PARAM,
	SUBSTANCE_TEXTURING_APP_PICKER_QUERY_PARAM,
	ALL_APPS_DEFAULT_DOWNLOAD,
	CCPP_DEFAULT_DOWNLOAD,
	CCPPN_DEFAULT_DOWNLOAD,
	SUBSTANCE_TEXTURING_DEFAULT_DOWNLOAD,
	SUBSTANCE_COLLECTION_DEFAULT_DOWNLOAD
} from '../../constants/AppPicker/appPickerConstants';
import {campaignId, singleAppPlans, nujM3SingleAppPlansJDI} from '../../constants/appsConstants';
import ContentParser from '@ccxi/ccxi-apps/src/utils/contentParser';
import Url from '../Url';
import getConfigApps from '../../config';
import {getLocalizedURL} from '@intl/adobe-locales';

class AppPickerUtil {
	pageType: string;
	appData: Object;
	contentfulData: Object;
	planDetails: Object;
	intentData: Object;

	init(data: Object, locale: string, intentData: Object) {
		if (AppsUtil.hasFeature('temp_contentful_to_aem_migration')) {
			this.contentfulData = data;
			this.planDetails = this.contentfulData.data.nujExperiencePostCheckoutByPath.item.planDetails;
		}
		else {
			this.contentfulData = this.processContentfulData(data);
			this.planDetails = this.contentfulData.items[0].planDetails;
		}
		this.appData = this.parseAppData(locale);
		this.intentData = intentData;
	}

	getContentFulMainObject() {
		if (AppsUtil.hasFeature('temp_contentful_to_aem_migration')) {
			return this.contentfulData.data.nujExperiencePostCheckoutByPath.item;
		}
		return this.contentfulData.items[0];
	}

	setPageType(type: string) {
		this.pageType = type;
	}

	getPageType() {
		return this.pageType;
	}

	processContentfulData(data: Object) {
		return ContentParser.parse(data);
	}

	parseAppData(locale: string) {
		const appData = {};
		const appNode = this.getContentFulMainObject().singleAppPlanApp;
		if (appNode) {
			appData[appNode.sapCode] = appNode;
		}
		else {
			const contentfulCategoriesList = this.getContentfulCategoriesList();
			for (const category of contentfulCategoriesList) {
				for (const task of category.taskList) {
					for (const app of task.appList) {
						appData[app.sapCode] = app;
					}
				}
			}
		}
		appData.KCCC = {
			systemRequirementsLink: getLocalizedURL(getConfigApps('EXTERNAL_URLS').SYSTEM_REQT_LINK_ALL_APPS, locale)
		};
		return appData;
	}

	getViewYourPlanModalContent() {
		return this.planDetails;
	}

	getActionList(sapCode: string, appName: string) {
		const actionList = [];
		if (!CompatibilityUtil.isAppSupported(sapCode)) {
			actionList.push({
				actionId: ACTION_IDS.LEARN_MORE,
				actionText: AppsUtil.intl.formatMessage({id: 'apps.allAppsAppPicker.card.learn-more'}),
				ariaLabel: AppsUtil.intl.formatMessage({id: 'apps.allAppsAppPicker.card.learn-more'}),
				icon: this.getIconFromIconType('error'),
				isQuiet: true,
				variant: 'secondary'
			});
		}
		actionList.push({
			actionId: ACTION_IDS.DOWNLOAD,
			actionText: AppsUtil.intl.formatMessage({id: 'apps.allAppsAppPicker.card.download'}),
			ariaLabel: `${AppsUtil.intl.formatMessage({id: 'apps.allAppsAppPicker.card.download'})} ${appName}`,
			isQuiet: false,
			isDisabled: !CompatibilityUtil.isAppSupported(sapCode) && !CompatibilityUtil.isAnyOlderVersionSupported(sapCode),
			variant: 'primary'
		});
		return actionList;
	}

	getIconFromIconType(iconType: string) {
		switch (iconType) {
			case 'error':
				return <Alert />;
			default:
				return null;
		}
	}

	getSupportedPlatformList(ffcProductData: Object) {
		const supportedPlatforms = ffcProductData.appLineage
			? AppsUtil.appLineageMap[ffcProductData.appLineage].platformTypeList
			: [AppsUtil.getProductPlatform(ffcProductData)];
		const platformObjList = [];
		supportedPlatforms.forEach((platform) => {
			platformObjList.push({
				id: platform,
				text: AppsUtil.intl.formatMessage({id: `apps.allAppsAppPicker.platform.${platform}`})
			});
		});
		return platformObjList;
	}

	getBannerImg(appData: Object) {
		if (window.devicePixelRatio > 2 && appData.bannerImage3x) {
			return AppsUtil.hasFeature('temp_contentful_to_aem_migration') ? appData.bannerImage3x._publishUrl : appData.bannerImage3x.file.url;
		}
		return AppsUtil.hasFeature('temp_contentful_to_aem_migration') ? appData.bannerImage2x._publishUrl : appData.bannerImage2x.file.url;
	}

	getConsolidatedAppCardData(ffcProductData: Object, appData: Object, locale: string) {
		return {
			title: (ffcProductData && ffcProductData.displayName) || appData.displayName,
			ffcShortDescription: [{
				description: ffcProductData && AppsUtil.getProductTagLine(ffcProductData, locale),
				showIcon: false
			}],
			productDetailsFromCVS: appData.productDetails,
			bannerSrc: this.getBannerImg(appData),
			wideBannerSrc: appData.wideBannerImage && (AppsUtil.hasFeature('temp_contentful_to_aem_migration') ? appData.wideBannerImage._publishUrl : appData.wideBannerImage.file.url),
			imageSrc: AppsUtil.getProductIcon(ffcProductData) || this.getProductIcon(appData.sapCode),
			supportedPlatform: (ffcProductData && this.getSupportedPlatformList(ffcProductData)) || [],
		};
	}

	getContentfulCategoriesList() {
		let categoryList = this.getContentFulMainObject().categoryList;
		if (!this.showRecommenededInDropdown()) {
			categoryList = categoryList.filter(category => category.id !== RECOMMENDED);
		}
		if (!this.showAllDesktopAppsInDropdown()) {
			categoryList = categoryList.filter(category => category.id !== ALL_APPS);
		}
		return categoryList;
	}

	getDefaultCategoryId() {
		if (this.showRecommenededInDropdown()) {
			return RECOMMENDED;
		}
		if (this.showAllDesktopAppsInDropdown()) {
			return ALL_APPS;
		}
		return this.getContentfulCategoriesList()[0].id;
	}

	getTaskObjectFromTaskId(inputTaskId: string, categoryId: string) {
		if (inputTaskId === DEFAULT_TASK_ID) {
			return this.getCategoryObjectFromCategoryId(categoryId).taskList.find(task => task.id.includes(inputTaskId));
		}
		return this.getCategoryObjectFromCategoryId(categoryId).taskList.find(task => task.id === inputTaskId);
	}

	getCategoryObjectFromCategoryId(catId: string) {
		return this.getContentfulCategoriesList().find(category => category.id === catId);
	}

	getDefaultCategorySapCodes() {
		const defaultCat = this.getCategoryObjectFromCategoryId(this.getDefaultCategoryId());
		const defaultTask = this.getTaskObjectFromTaskId(DEFAULT_TASK_ID, defaultCat.id);
		const defaultCategorySapCodeList = [];
		for (const app of defaultTask.appList) {
			defaultCategorySapCodeList.push(app.sapCode);
		}
		return defaultCategorySapCodeList;
	}

	getAllCategoriesSapCodes() {
		const contentfulCategoriesList = this.getContentfulCategoriesList();
		const allSapCodeList = [];
		for (const category of contentfulCategoriesList) {
			for (const task of category.taskList) {
				for (const app of task.appList) {
					if (!allSapCodeList.find(sapCode => sapCode === app.sapCode)) {
						allSapCodeList.push(app.sapCode);
					}
				}
			}
		}
		return allSapCodeList;
	}

	shouldRenderOnlyCategories() {
		const contentfulCategoriesList = this.getContentfulCategoriesList();
		let renderOnlyCategories = false;
		for (const category of contentfulCategoriesList) {
			if (category.taskList.length === 1) {
				renderOnlyCategories = true;
				break;
			}
		}
		return renderOnlyCategories;
	}

	getAppData(sapCode: string) {
		return this.appData[sapCode];
	}

	getSystemRequirementLink(sapCode: string) {
		return this.appData && this.appData[sapCode] && this.appData[sapCode].systemRequirementsLink;
	}

	getDisplayName(sapCode: string) {
		return this.appData && this.appData[sapCode] && this.appData[sapCode].displayName;
	}

	getProductIcon(sapCode: string) {
		return this.appData && this.appData[sapCode] && this.appData[sapCode].productIcon && this.appData[sapCode].productIcon.file.url;
	}

	isAllAppsPlan() {
		return this.getAppPickerPlan() === ALL_APPS_PLAN;
	}

	isCcppPlan() {
		return this.getAppPickerPlan() === CCPP_PLAN;
	}

	isCcppnPlan() {
		return this.getAppPickerPlan() === CCPPN_PLAN;
	}

	isSubstancePlan() {
		return [SUBSTANCE_COLLECTION_PLAN, SUBSTANCE_TEXTURING_PLAN].includes(this.getAppPickerPlan());
	}

	getPlanDefaultDownloadList() {
		if (this.showRecommenededInDropdown()) {
			const appWithAffinity = this.getUserAffinitySapcode();
			const appList = [appWithAffinity, ...this.getAppData(appWithAffinity).moreApps];
			return appList;
		}
		switch (this.getAppPickerPlan()) {
			case ALL_APPS_PLAN:
				return ALL_APPS_DEFAULT_DOWNLOAD;
			case CCPP_PLAN:
				return CCPP_DEFAULT_DOWNLOAD;
			case CCPPN_PLAN:
				return CCPPN_DEFAULT_DOWNLOAD;
			case SUBSTANCE_TEXTURING_PLAN:
				return SUBSTANCE_TEXTURING_DEFAULT_DOWNLOAD;
			case SUBSTANCE_COLLECTION_PLAN:
				return SUBSTANCE_COLLECTION_DEFAULT_DOWNLOAD;
		}
		return [];
	}

	getDefaultDownloadProduct() {
		const defaultDnldList = this.getPlanDefaultDownloadList();
		const defaultProduct = defaultDnldList.find(
			sapCode => CompatibilityUtil.isAppSupported(sapCode) || (!CompatibilityUtil.isOSDeprecated() && CompatibilityUtil.isAnyOlderVersionSupported(sapCode))
		);
		return defaultProduct || 'KCCC';
	}

	/**
	 * Return true if even one of the product in the list is available
	 */
	isDefaultDownloadProductFFCDataAvailable() {
		const defaultDnldList = this.getPlanDefaultDownloadList();
		const defaultProduct = defaultDnldList.find(
			sapCode => !!AppsUtil.getFFCProductConfigBySapCode(sapCode)
		);
		return !!defaultProduct;
	}

	getSourceQueryParam() {
		switch (this.getAppPickerPlan()) {
			case ALL_APPS_PLAN:
				return ALL_APPS_APP_PICKER_QUERY_PARAM;
			case CCPP_PLAN:
				return CCPP_APP_PICKER_QUERY_PARAM;
			case CCPPN_PLAN:
				return CCPPN_APP_PICKER_QUERY_PARAM;
			case SUBSTANCE_TEXTURING_PLAN:
				return SUBSTANCE_TEXTURING_APP_PICKER_QUERY_PARAM;
			case SUBSTANCE_COLLECTION_PLAN:
				return SUBSTANCE_COLLECTION_APP_PICKER_QUERY_PARAM;
		}
		return '';
	}

	getPlanFromDownloadUrl() {
		const isDownloadUrl = ((AppsUtil.getCCWebPathData().isPDP && AppsUtil.getCCWebPathData().product) || Url.getParam('productId')) && Url.getParam('action') === 'install';
		if (!isDownloadUrl) {
			return 'other';
		}
		switch (Url.getParam('source')) {
			case ALL_APPS_APP_PICKER_QUERY_PARAM:
				return ALL_APPS_PLAN;
			case CCPP_APP_PICKER_QUERY_PARAM:
				return CCPP_PLAN;
			case CCPPN_APP_PICKER_QUERY_PARAM:
				return CCPPN_PLAN;
			case SUBSTANCE_TEXTURING_APP_PICKER_QUERY_PARAM:
				return SUBSTANCE_TEXTURING_PLAN;
			case SUBSTANCE_COLLECTION_APP_PICKER_QUERY_PARAM:
				return SUBSTANCE_COLLECTION_PLAN;
		}
	}

	getAppPickerPlan() {
		let plan = AppsUtil.getPathData().product;
		if ([CCPP_PLAN, CCPPN_PLAN, ALL_APPS_PLAN, SUBSTANCE_COLLECTION_PLAN, SUBSTANCE_TEXTURING_PLAN].indexOf(plan) === -1) {
			plan = this.getPlanFromDownloadUrl();
		}
		return plan;
	}

	isNUJDeprecatedOS() {
		/**
		 * This check is for the OSes mentioned in https://wiki.corp.adobe.com/display/CCEE/M1+Learnings+-+All+Apps+App+Picker
		 * For these OSes we show a special screen
		 */
		if (AppsUtil.hasFeature('bypass_compatibility_check')) {
			// This is for monitoring whcih run on Linux machines
			return false;
		}
		if (DeviceDetectionUtil.isWin()) {
			if (this.isAllAppsPlan() || this.isCcppPlan() || this.isCcppnPlan()) {
				// For all apps and photo plans, win 7 and 8 are unsupported for most apps
				return DeviceDetectionUtil.isWin7or8();
			}
			else if (this.isSubstancePlan()) {
				return DeviceDetectionUtil.getOSVersion() === '7';
			}
		}
		else if (DeviceDetectionUtil.isMacOS()) {
			const macOS = DeviceDetectionUtil.getMacVersion();
			if (macOS && macOS !== null) {
				const macOSArr = macOS.split('.');
				return (macOSArr[0] === '10' && macOSArr[1] && macOSArr[1] === '11')
					|| (parseInt(macOSArr[0], 10) < 10 || (macOSArr[0] === '10' && macOSArr[1] && parseInt(macOSArr[1], 10) < 11));
			}
		}
		return (DeviceDetectionUtil.isLinux() || DeviceDetectionUtil.isChromeOS());
	}

	showAllDesktopAppsInDropdown() {
		return this.isAllAppsPlan() && this.isNUJDeprecatedOS();
	}

	showRecommenededInDropdown() {
		// Show recommended apps only where we get app affinity info
		const appAffinitySapCode = this.getUserAffinitySapcode();
		return (
			AppsUtil.hasFeature('temp_cch_7467')
			&& this.isAllAppsPlan()
			&& !!appAffinitySapCode
			&& (
				CompatibilityUtil.isAppSupported(appAffinitySapCode)
				|| (!CompatibilityUtil.isOSDeprecated() && CompatibilityUtil.isAnyOlderVersionSupported(appAffinitySapCode))
			)
		);
	}

	getUserAffinitySapcode() {
		// Check if intent present for any app in the Intents data
		let userAffinitySapcode;
		if (this.intentData && this.intentData.apps && Object.keys(this.intentData.apps).length) {
			// Intent for some app present
			const sapCodeList = Object.keys(this.intentData.apps);
			userAffinitySapcode = sapCodeList.find(sapCode => this.intentData.apps[sapCode].appAffinity.score === 0.8);
		}
		return userAffinitySapcode;
	}

	addAppPickerClassesAndEvents(ccIconEventCb: Function, chatEventCb: Function, handleCCEnter: Function) {
		CSSUtil.addAppPickerClassToAppContainer();
		CSSUtil.addEventListenerToElem(document.getElementsByClassName('navbar-title-span')[0], 'click', ccIconEventCb);
		CSSUtil.addEventListenerToElem(document.getElementsByClassName('navbar-title-span')[0], 'keydown', handleCCEnter);
		AppsUtil.addJarvisEventCallback('click', chatEventCb);
	}

	removeAppPickerClassesAndEvents(ccIconEventCb: Function, chatEventCb: Function, handleCCEnter: Function) {
		CSSUtil.removeAppPickerClassToAppContainer();
		CSSUtil.removeEventListenerFromElem(document.getElementsByClassName('navbar-title-span')[0], 'click', ccIconEventCb);
		CSSUtil.removeEventListenerFromElem(document.getElementsByClassName('navbar-title-span')[0], 'keydown', handleCCEnter);
		AppsUtil.removeJarvisEventCallback('click', chatEventCb);
	}

	hideLanguageSelectorInFooter() {
		// Show language selector in all cases except for cases where A/B test for EN only is running
		if (this.isNujM3PhotoPlanTestExperience()) {
			return true;
		}
		return (
			isTestExperience(campaignId.nujM3Test)
			&& singleAppPlans.filter(app => !nujM3SingleAppPlansJDI.includes(app)).includes(AppsUtil.getSingleAppPostPurchasePlan())
		);
	}

	isNUJAppPickerExperience() {
		return this.isNUJAllAppsExperience() || this.isNujM3PhotoPlanTestExperience() || this.isNUJSubstancePlanExperience();
	}

	isNUJAllAppsExperience() {
		return !(DeviceDetectionUtil.isMobileOrTab() && !DeviceDetectionUtil.isChromeOS())
			&& (this.isAllAppsPlan());
	}

	isNUJSubstancePlanExperience() {
		if (!AppsUtil.hasFeature('temp_onboard_3d_apps')) {
			return false;
		}
		return !(DeviceDetectionUtil.isMobileOrTab() && !DeviceDetectionUtil.isChromeOS())
			&& (this.isSubstancePlan());
	}

	isNujM3PhotoPlanTestExperience() {
		if (!AppsUtil.hasFeature('temp_nuj_m3_photo_plan_changes')) {
			return false;
		}
		return isTestExperience(campaignId.nujM3PhotoPlanTest)
			&& (getExperienceForCampaign(campaignId.nujM3PhotoPlanTest) === EXPERIENCE_TYPE.PHOTO_PLAN)
			&& this.isCcppPlan();
	}

	isSingleAppPlanNUJExp() {
		if (!AppsUtil.hasFeature('temp_nuj_m3_fg_changes')) {
			return false;
		}
		return (isTestExperience(campaignId.nujM3Test)
			&& getExperienceForCampaign(campaignId.nujM3Test) === `${AppsUtil.getSingleAppPostPurchasePlan() || 'null'}-plan`)
			|| (AppsUtil.isNujM3JdiSingleApp(AppsUtil.getSingleAppPostPurchasePlan()) && !DeviceDetectionUtil.isMobileOrTab());
	}
}

export default new AppPickerUtil();
